
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { FilteringSortingTableStore } from "@/mixins/tableWithFilteringAndSortingStore";
import { DataModules } from "@/store/modules/data";
import { CreateElement } from "vue";
import { IClient } from "@/models/clients";
import {
  IClientsProvider,
  IClientsProviderMethods
} from "@/models/providers/client";
import { ISorter, IFilter } from "@/models/tables";
import { DefaultFilters } from "@/data/filters/client";
import { DefaultSorters } from "@/data/sorters/client";
import CustomFieldsMixin from "@/mixins/customFieldsMixin";
import TagsMixin from "@/mixins/tagsMixin";
import { ITag } from "@/models/tags";
import { mapState } from "vuex";
import { IState } from "@/store";

@Component({
  computed: {
    ...mapState({
      clientsWithParam(state: IState, getters) {
        return getters[`data/${DataModules.CLIENTS}/withParam`]();
      }
    })
  }
})
export default class ClientsProvider extends Mixins(
  FilteringSortingTableStore,
  CustomFieldsMixin,
  TagsMixin
) {
  // Props

  @Prop({ default: `CLP` /** [C]lients [L]ist [P]rovider */ })
  public declare id: string;

  @Prop({ default: false })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => DefaultFilters() })
  public availableFilters!: IFilter[];

  @Prop({ default: () => DefaultSorters() })
  public availableSorters!: ISorter[];

  @Prop({ default: () => ({}) })
  public readonly manualFilters!: Record<string, any>;

  @Prop({ default: 12 })
  public declare limit: number;

  // Data

  public guardFunctionalities = ["list_clients", "list_client_fields"];
  public dataModule = DataModules.CLIENTS;
  public debounceSearch = this.$_.debounce(this.doQuickSearch, 250);
  public searchQuery = "";
  public filters = this.availableFilters;
  public sorters = this.availableSorters;
  public isReloading = false;
  public clientsWithParam!: string;

  // Life-cycle Methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.loadData();
    this.$bus.$on("admin/client/tags/refresh", this.onTagsRefresh);
  }

  public beforeDestroy() {
    this.$bus.$off("admin/client/tags/refresh", this.onTagsRefresh);
  }

  public loadData() {
    return Promise.all([
      this.getData(),
      this.getClientCustomFields(this.isReloading),
      this.getTags(this.isReloading).then(() => {
        this.filters.push(
          this.createTagFilter("tags.id", this.$t("_.tag"), "")
        );
      })
    ]);
  }

  public onTagsRefresh() {
    this.reloadData();
  }

  public async reloadData() {
    try {
      this.isReloading = true;
      await this.cancelRequests();
      return this.getData({ ignoreStored: true });
    } finally {
      this.isReloading = false;
    }
  }

  // Computed
  public get queryFilter() {
    return this.searchQuery ? { query: this.searchQuery } : {};
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Methods

  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        params: {
          with: this.clientsWithParam,
          ...this.queryFilter,
          ...this.manualFilters
        }
      },
      params // Important – must merge last
    );
  }

  public async doQuickSearch(value = "") {
    if (value === this.searchQuery) return Promise.resolve();
    this.searchQuery = value;
    return this.getData({
      ignoreStored: true,
      page: 1
    });
  }

  public applyTagFilter(tag: ITag) {
    let tagFilter = this.$_.find(this.activeFilters, ["key", "tags.id"]);
    if (!tagFilter) {
      tagFilter = this.createTagFilter(
        "tags.id",
        this.$t("_.client_tag"),
        "client"
      );
    }
    this.$set(tagFilter, "value", [tag.id]);
    this.onFiltersChange([tagFilter]);
  }

  // Provide

  @Provide("clientsProviderMethods")
  public get providerMethods(): IClientsProviderMethods {
    return {
      applyTagFilter: this.applyTagFilter,
      debounceSearch: this.debounceSearch,
      doQuickSearch: this.doQuickSearch,
      getData: this.getData,
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      openAddClientModal: () =>
        this.$store.dispatch(`data/${DataModules.CLIENTS}/openAddClientModal`),
      reloadData: this.reloadData
    };
  }

  @Provide("clientsProvider")
  public providerData(): IClientsProvider {
    return {
      availableFilters: this.filters,
      availableSorters: this.sorters,
      activeFilters: this.activeFilters,
      constQueryFilters: this.constQueryFilters,
      activeSort: this.table.sort,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasClients: !!this.table.data.length,
      isLoading: this.table.loading,
      isReloading: this.isReloading,
      limit: this.table.limit,
      page: this.table.page,
      clients: this.table.data as IClient[],
      searchQuery: this.searchQuery,
      table: this.table,
      total: this.table.total,
      guardFunctionalities: this.guardFunctionalities
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });

    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.loadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $clientsData: this.providerData(),
        $clientsMethods: this.providerMethods
      })
    );
  }
}
